import { db, newId, type Section } from './schema';
import { deleteImages, storeImage } from './images';
import { isWithin, type Container } from './board';

/**
 * Writing sections. Reading them is `board.ts`.
 *
 * Items keep their `roomId` at every depth, so none of this moves a photo —
 * it only ever rewrites which container something points at. Dissolving a
 * section is a parent swap, not a deletion.
 */

export type SectionDraft = {
  name: string;
  cover?: Blob | undefined;
};

export type SectionEdit = {
  name: string;
  /** A replacement file, `null` to clear the cover, `undefined` to leave it. */
  cover?: Blob | null | undefined;
};

/** Shown wherever a section hasn't been given a name. */
export const UNNAMED = 'Unnamed Section';

export function sectionName(section: { name: string }): string {
  return section.name.trim() === '' ? UNNAMED : section.name.trim();
}

export async function createSection(
  container: Container,
  draft: SectionDraft,
): Promise<string> {
  // Outside the transaction: canvas encoding is slow enough to time one out.
  const coverImageId = draft.cover === undefined ? undefined : await storeImage(draft.cover);

  const section: Section = {
    id: newId(),
    roomId: container.roomId,
    parentId: container.sectionId,
    name: draft.name.trim(),
    coverImageId,
    sort: await nextSort(container),
    createdAt: Date.now(),
  };

  await db.sections.add(section);
  return section.id;
}

export async function updateSection(id: string, edit: SectionEdit): Promise<void> {
  const replacement = edit.cover instanceof Blob ? await storeImage(edit.cover) : undefined;

  await db.transaction('rw', db.sections, db.images, async () => {
    const section = await db.sections.get(id);
    if (section === undefined) return;

    const changes: Partial<Section> = { name: edit.name.trim() };

    if (edit.cover !== undefined) {
      changes.coverImageId = replacement;
      if (section.coverImageId !== undefined) await db.images.delete(section.coverImageId);
    }

    await db.sections.update(id, changes);
  });
}

/**
 * The section goes; everything in it moves up to where the section was.
 *
 * Not a cascade like deleting a room. A section is a way of arranging things
 * you already own — dropping the arrangement should never drop the things,
 * and the dialog says so before it happens.
 */
export async function deleteSection(id: string): Promise<void> {
  const cover = await db.transaction('rw', db.sections, db.items, async () => {
    const section = await db.sections.get(id);
    if (section === undefined) return undefined;

    const parentId = section.parentId;
    const children = await db.sections.where('parentId').equals(id).toArray();
    const items = await db.items.where('sectionId').equals(id).toArray();

    await Promise.all([
      ...children.map((child) => db.sections.update(child.id, { parentId })),
      ...items.map((item) => db.items.update(item.id, { sectionId: parentId })),
    ]);

    await db.sections.delete(id);
    return section.coverImageId;
  });

  if (cover !== undefined) await deleteImages([cover]);
}

/** Puts an item or a section inside `sectionId`, at the top of it. */
export async function moveInto(
  entryId: string,
  kind: 'item' | 'section',
  sectionId: string | undefined,
): Promise<void> {
  if (kind === 'section' && sectionId !== undefined) {
    // A section can't be put inside itself, or inside its own descendant —
    // the branch would leave the tree with it.
    if (await isWithin(sectionId, entryId)) return;
  }

  const roomId =
    kind === 'item'
      ? (await db.items.get(entryId))?.roomId
      : (await db.sections.get(entryId))?.roomId;
  if (roomId === undefined) return;

  const sort = await nextSort({ roomId, sectionId });

  if (kind === 'item') await db.items.update(entryId, { sectionId, sort });
  else await db.sections.update(entryId, { parentId: sectionId, sort });
}

/**
 * Two things dropped on each other become a section holding both. The new
 * section takes the target's place in the order, the way a folder appears
 * where the app you dropped onto was.
 */
export async function groupInto(
  container: Container,
  dragged: { id: string; kind: 'item' | 'section' },
  target: { id: string; kind: 'item' | 'section' },
): Promise<string | undefined> {
  if (dragged.id === target.id) return undefined;
  if (dragged.kind === 'section' && target.kind === 'section') {
    if (await isWithin(target.id, dragged.id)) return undefined;
  }

  const sort = await sortOf(target);
  if (sort === undefined) return undefined;

  const id = newId();
  await db.sections.add({
    id,
    roomId: container.roomId,
    parentId: container.sectionId,
    name: '',
    coverImageId: undefined,
    sort,
    createdAt: Date.now(),
  });

  // The target first, the dragged one on top of it: what you just moved is
  // the newest thing in there, and the cover reads newest first.
  await place(target, id, 0);
  await place(dragged, id, 1);
  return id;
}

/**
 * Writes a dragged arrangement back across both tables. Sections and items
 * share one run of `sort` values per container, so they interleave.
 */
export async function reorderEntries(orderedIds: readonly string[]): Promise<void> {
  await db.transaction('rw', db.items, db.sections, async () => {
    const top = orderedIds.length - 1;

    await Promise.all(
      orderedIds.map(async (id, index) => {
        const sort = top - index;
        const item = await db.items.get(id);
        if (item !== undefined) {
          if (item.sort !== sort) await db.items.update(id, { sort });
          return;
        }

        const section = await db.sections.get(id);
        if (section !== undefined && section.sort !== sort) {
          await db.sections.update(id, { sort });
        }
      }),
    );
  });
}

/** One run of numbers per container, counting both tables. */
export async function nextSort(container: Container): Promise<number> {
  const [items, sections] = await Promise.all([
    db.items.where('roomId').equals(container.roomId).toArray(),
    db.sections.where('roomId').equals(container.roomId).toArray(),
  ]);

  const here = [
    ...items.filter((item) => item.sectionId === container.sectionId),
    ...sections.filter((section) => section.parentId === container.sectionId),
  ];

  return here.reduce((top, row) => Math.max(top, row.sort + 1), 0);
}

async function sortOf(entry: { id: string; kind: 'item' | 'section' }): Promise<number | undefined> {
  const row =
    entry.kind === 'item' ? await db.items.get(entry.id) : await db.sections.get(entry.id);
  return row?.sort;
}

async function place(
  entry: { id: string; kind: 'item' | 'section' },
  sectionId: string,
  sort: number,
): Promise<void> {
  if (entry.kind === 'item') await db.items.update(entry.id, { sectionId, sort });
  else await db.sections.update(entry.id, { parentId: sectionId, sort });
}
